import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import { Building2, Users, CheckCircle, ToggleLeft, ToggleRight } from 'lucide-react';

const TenantManagement = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    tenant_name: '',
    tenant_code: '',
    contact_email: '',
    contact_phone: '',
    address: ''
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.tenants.list();
      setTenants(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load tenants');
      console.error('Error fetching tenants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTenant) {
        await adminAPI.tenants.update(editingTenant.tenant_id, formData);
      } else {
        await adminAPI.tenants.create(formData);
      }
      setShowCreateModal(false);
      setEditingTenant(null);
      setFormData({
        tenant_name: '',
        tenant_code: '',
        contact_email: '',
        contact_phone: '',
        address: ''
      });
      fetchTenants();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save tenant');
    }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      tenant_name: tenant.tenant_name,
      tenant_code: tenant.tenant_code,
      contact_email: tenant.contact_email || '',
      contact_phone: tenant.contact_phone || '',
      address: tenant.address || ''
    });
    setShowCreateModal(true);
  };

  const handleToggleStatus = async (tenant) => {
    const action = tenant.is_active ? 'deactivate' : 'activate';
    const warning = !tenant.is_active ? '' : '\n\nAll users of this tenant will be blocked from logging in.';
    if (window.confirm(`Are you sure you want to ${action} "${tenant.tenant_name}"?${warning}`)) {
      try {
        await adminAPI.tenants.toggleStatus(tenant.tenant_id);
        fetchTenants();
      } catch (err) {
        alert(err.response?.data?.detail || `Failed to ${action} tenant`);
      }
    }
  };

  const handleManageUsers = (tenantId) => {
    navigate(`/admin/users?tenant=${tenantId}`);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const styles = `
    .page-header {
      margin-bottom: 32px;
    }

    .page-title {
      font-size: 28px;
      font-weight: 800;
      color: hsl(var(--foreground));
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .page-subtitle {
      font-size: 14px;
      color: hsl(var(--muted-foreground));
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 20px;
      margin-bottom: 32px;
    }

    .action-bar {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 24px;
    }

    .btn-primary {
      padding: 12px 24px;
      background: var(--gradient-primary);
      color: white;
      border: none;
      border-radius: var(--radius-md);
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
      box-shadow: var(--shadow-glow);
      position: relative;
      overflow: hidden;
    }

    .btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(139, 92, 246, 0.4);
    }

    .btn-primary:active {
      transform: translateY(0);
    }

    .btn-secondary {
      padding: 12px 24px;
      background: transparent;
      color: hsl(var(--foreground));
      border: 2px solid hsl(var(--border));
      border-radius: var(--radius-md);
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
      position: relative;
      overflow: hidden;
    }

    .btn-secondary:hover {
      background: hsl(var(--secondary));
      border-color: hsl(var(--primary));
      color: hsl(var(--primary));
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .btn-secondary:active {
      transform: translateY(0);
    }

    .content-card {
      background: var(--gradient-card);
      border-radius: var(--radius-lg);
      border: 1px solid hsl(var(--border));
      box-shadow: var(--shadow-card);
      overflow: hidden;
    }

    .card-header {
      padding: 20px 24px;
      border-bottom: 1px solid hsl(var(--border));
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .card-title {
      font-size: 18px;
      font-weight: 700;
      color: hsl(var(--foreground));
    }

    .data-table {
      width: 100%;
      border-collapse: collapse;
    }

    .data-table thead {
      background: hsl(var(--secondary) / 0.5);
    }

    .data-table th {
      padding: 14px 24px;
      text-align: left;
      font-size: 11px;
      font-weight: 700;
      color: hsl(var(--muted-foreground));
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .data-table tbody tr {
      border-bottom: 1px solid hsl(var(--border));
      transition: background 0.2s;
    }

    .data-table tbody tr:hover {
      background: hsl(var(--secondary) / 0.3);
    }

    .data-table td {
      padding: 16px 24px;
      font-size: 14px;
      color: hsl(var(--foreground));
    }

    .badge {
      padding: 4px 12px;
      border-radius: var(--radius-full);
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .badge-secondary {
      background: hsl(var(--secondary));
      color: hsl(var(--foreground));
    }

    .status-active {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 10px;
      border-radius: var(--radius-full);
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      background: hsl(var(--success) / 0.15);
      color: hsl(var(--success));
    }

    .status-inactive {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 10px;
      border-radius: var(--radius-full);
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      background: hsl(var(--destructive) / 0.15);
      color: hsl(var(--destructive));
    }

    .actions-cell {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .btn-icon {
      width: 36px;
      height: 36px;
      border: none;
      border-radius: var(--radius-md);
      font-size: 16px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .btn-manage {
      background: hsl(var(--primary) / 0.15);
      color: hsl(var(--primary));
    }

    .btn-manage:hover {
      background: hsl(var(--primary) / 0.25);
      transform: scale(1.05);
    }

    .btn-edit {
      background: hsl(var(--warning) / 0.15);
      color: hsl(var(--warning));
    }

    .btn-edit:hover {
      background: hsl(var(--warning) / 0.25);
      transform: scale(1.05);
    }

    .btn-delete {
      background: hsl(var(--destructive) / 0.15);
      color: hsl(var(--destructive));
    }

    .btn-delete:hover {
      background: hsl(var(--destructive) / 0.25);
      transform: scale(1.05);
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
    }

    .empty-icon {
      font-size: 48px;
      opacity: 0.3;
      margin-bottom: 16px;
    }

    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 20px;
      backdrop-filter: blur(8px);
    }

    .modal-content {
      background: hsl(var(--background));
      border-radius: var(--radius-lg);
      max-width: 600px;
      width: 100%;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    }

    .modal-header {
      padding: 24px;
      border-bottom: 2px solid hsl(var(--border));
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: hsl(var(--secondary) / 0.3);
    }

    .modal-title {
      font-size: 20px;
      font-weight: 700;
      color: hsl(var(--foreground));
      margin: 0;
    }

    .btn-close {
      width: 40px;
      height: 40px;
      border-radius: var(--radius-full);
      border: none;
      background: hsl(var(--secondary));
      color: hsl(var(--foreground));
      font-size: 20px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;
    }

    .btn-close:hover {
      transform: rotate(90deg) scale(1.1);
      background: hsl(var(--destructive) / 0.15);
      color: hsl(var(--destructive));
    }

    .modal-form {
      padding: 24px;
    }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      margin-bottom: 20px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .form-group label {
      font-size: 13px;
      font-weight: 600;
      color: hsl(var(--foreground));
    }

    .form-group input,
    .form-group textarea {
      padding: 10px 14px;
      border: 2px solid hsl(var(--border));
      border-radius: var(--radius-md);
      font-size: 14px;
      color: hsl(var(--foreground));
      background: hsl(var(--background));
      transition: all 0.2s;
    }

    .form-group input:focus,
    .form-group textarea:focus {
      outline: none;
      border-color: hsl(var(--primary));
      box-shadow: 0 0 0 3px hsl(var(--primary) / 0.1);
    }

    .modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding-top: 20px;
      border-top: 1px solid hsl(var(--border));
    }

    .loading-container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: hsl(var(--background));
    }

    .error-alert {
      padding: 16px 20px;
      background: hsl(var(--destructive) / 0.15);
      border: 1px solid hsl(var(--destructive) / 0.3);
      border-radius: var(--radius-lg);
      color: hsl(var(--destructive));
      margin-bottom: 24px;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="loading-container">
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
            <div style={{ fontSize: '14px', color: 'hsl(var(--muted-foreground))' }}>
              Loading tenants...
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <DashboardLayout user={user} logout={logout}>
        <div className="page-header">
          <h1 className="page-title">
            <Building2 size={28} />
            Tenant Management
          </h1>
          <p className="page-subtitle">Manage companies and organizations</p>
        </div>

        {error && (
          <div className="error-alert">
            <span style={{ fontSize: '20px' }}>⚠️</span>
            {error}
          </div>
        )}

        {/* Stats */}
        <motion.div
          className="stats-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="stat-card">
            <div className="icon-container">
              <Building2 size={20} />
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '800', color: 'hsl(var(--foreground))', margin: '16px 0 4px' }}>
              {tenants.length}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Tenants
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="stat-card">
            <div className="icon-container" style={{ background: 'var(--gradient-success)' }}>
              <CheckCircle size={20} />
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '800', color: 'hsl(var(--success))', margin: '16px 0 4px' }}>
              {tenants.filter(t => t.is_active).length}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Active Tenants
            </p>
          </motion.div>
        </motion.div>

        {/* Action Bar */}
        <div className="action-bar">
          <button
            className="btn-primary"
            onClick={() => {
              setEditingTenant(null);
              setFormData({
                tenant_name: '',
                tenant_code: '',
                contact_email: '',
                contact_phone: '',
                address: ''
              });
              setShowCreateModal(true);
            }}
          >
            + Create New Tenant
          </button>
        </div>

        {/* Tenants Table */}
        <div className="content-card">
          <div className="card-header">
            <h2 className="card-title">All Tenants</h2>
          </div>

          {tenants.length === 0 ? (
            <div className="empty-state">
              <Building2 size={48} style={{ opacity: 0.3, margin: '0 auto 16px' }} />
              <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '16px' }}>No tenants found</p>
              <button
                className="btn-secondary"
                onClick={() => setShowCreateModal(true)}
              >
                Create Your First Tenant
              </button>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Company Name</th>
                  <th>Code</th>
                  <th>Contact Email</th>
                  <th>Contact Phone</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant, index) => (
                  <tr key={tenant.tenant_id}>
                    <td style={{ fontWeight: '600', color: 'hsl(var(--muted-foreground))' }}>
                      {index + 1}
                    </td>
                    <td>
                      <strong>{tenant.tenant_name}</strong>
                    </td>
                    <td>
                      <span className="badge badge-secondary">{tenant.tenant_code}</span>
                    </td>
                    <td style={{ color: 'hsl(var(--muted-foreground))' }}>
                      {tenant.contact_email || '-'}
                    </td>
                    <td style={{ color: 'hsl(var(--muted-foreground))' }}>
                      {tenant.contact_phone || '-'}
                    </td>
                    <td>
                      {tenant.is_active ? (
                        <span className="status-active">Active</span>
                      ) : (
                        <span className="status-inactive">Inactive</span>
                      )}
                    </td>
                    <td>{new Date(tenant.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="actions-cell">
                        <button
                          className="btn-icon btn-manage"
                          onClick={() => handleManageUsers(tenant.tenant_id)}
                          title="Manage Users"
                        >
                          <Users size={16} />
                        </button>
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(tenant)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className={`btn-icon ${tenant.is_active ? 'btn-delete' : 'btn-manage'}`}
                          onClick={() => handleToggleStatus(tenant)}
                          title={tenant.is_active ? 'Deactivate Tenant' : 'Activate Tenant'}
                        >
                          {tenant.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">
                  {editingTenant ? '✏️ Edit Tenant' : '➕ Create New Tenant'}
                </h2>
                <button className="btn-close" onClick={() => setShowCreateModal(false)}>
                  ✕
                </button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Company Name *</label>
                    <input
                      type="text"
                      name="tenant_name"
                      value={formData.tenant_name}
                      onChange={handleInputChange}
                      placeholder="e.g., FamilySearch Inc"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Tenant Code *</label>
                    <input
                      type="text"
                      name="tenant_code"
                      value={formData.tenant_code}
                      onChange={handleInputChange}
                      placeholder="e.g., FS001"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Contact Email *</label>
                    <input
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleInputChange}
                      placeholder="contact@company.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Contact Phone</label>
                    <input
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleInputChange}
                      placeholder="+1-555-0123"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter company address..."
                    rows="3"
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingTenant ? 'Update Tenant' : 'Create Tenant'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </DashboardLayout>
    </>
  );
};

export default TenantManagement;
